import { Entity } from "@cohesive/api-entities";
import { motion } from "framer-motion";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EntityCard } from "../EntityCards/EntityCard/EntityCard";
import { Markdown } from "../Markdown";

export interface MessageRendererProps {
  content: string;
  entities?: Map<string, Entity>;
  isStreaming?: boolean;
  isAssistant?: boolean;
  onEntityClick?: (entity: Entity) => void;
  onEntityMentionClick?: (entity: Entity) => void;
  /** Optional custom entity renderer. Return null to use default rendering. */
  renderEntity?: (
    entity: Entity,
    onClick?: () => void,
    onMentionClick?: () => void,
  ) => React.ReactNode | null;
  className?: string;
}

// Render entity references
const RenderEntityReference = React.memo(
  ({
    entity,
    onClick,
    onMentionClick,
    customRenderer,
  }: {
    entity: Entity;
    onClick?: () => void;
    onMentionClick?: () => void;
    customRenderer?: (
      entity: Entity,
      onClick?: () => void,
      onMentionClick?: () => void,
    ) => React.ReactNode | null;
  }) => {
    // Try custom renderer first if provided
    if (customRenderer) {
      const customRendered = customRenderer(entity, onClick, onMentionClick);
      if (customRendered !== null) {
        return <>{customRendered}</>;
      }
    }

    if (!entity) {
      return null;
    }

    return (
      <EntityCard
        entity={entity}
        onClick={onClick}
        onMentionClick={onMentionClick}
      />
    );
  },
);

RenderEntityReference.displayName = "RenderEntityReference";

// Memoized content renderer component
const ContentWithEntities = React.memo(
  ({
    bufferedContent,
    renderEntityReference,
    isStreaming,
    isAssistant,
  }: {
    bufferedContent: string;
    renderEntityReference: (params: {
      entityId: string;
      entityType: string;
      keyPrefix: string;
    }) => React.ReactNode;
    isStreaming: boolean;
    isAssistant: boolean;
  }) => {
    // Render text with markdown, optionally with streaming effect
    const renderText = (text: string, key: string) => {
      return (
        <Markdown key={key} isStreaming={isStreaming && isAssistant}>
          {text}
        </Markdown>
      );
    };

    const content = bufferedContent;
    const elements = [];

    // Detect partial entity reference at end (for streaming)
    const partialEntityRegex = /<e[^>]*$/;
    const partialMatch = content.match(partialEntityRegex);

    if (partialMatch) {
      // Handle content before partial reference
      const beforePartial = content.slice(0, partialMatch.index);

      if (beforePartial.trim()) {
        const beforeParts = beforePartial.split(
          /<entity-ref\s+id="([^"]+)"\s+type="([^"]+)"\s*\/>/g,
        );

        for (let i = 0; i < beforeParts.length; i += 3) {
          if (beforeParts[i]) {
            elements.push(renderText(beforeParts[i], `text-${i}`));
          }

          if (beforeParts[i + 1] && beforeParts[i + 2]) {
            elements.push(
              renderEntityReference({
                entityId: beforeParts[i + 1],
                entityType: beforeParts[i + 2],
                keyPrefix: `entity-${i}`,
              }),
            );
          }
        }
      }

      return <>{elements}</>;
    }

    // Handle complete entity references
    const parts = content.split(
      /<entity-ref\s+id="([^"]+)"\s+type="([^"]+)"\s*\/>/g,
    );

    for (let i = 0; i < parts.length; i += 3) {
      if (parts[i]) {
        elements.push(renderText(parts[i], `text-${i}`));
      }

      if (parts[i + 1] && parts[i + 2]) {
        elements.push(
          renderEntityReference({
            entityId: parts[i + 1],
            entityType: parts[i + 2],
            keyPrefix: `entity-${i}`,
          }),
        );
      }
    }

    return <>{elements}</>;
  },
);

ContentWithEntities.displayName = "ContentWithEntities";

export const MessageRenderer: React.FC<MessageRendererProps> = React.memo(
  ({
    content,
    entities = new Map<string, Entity>(),
    isStreaming = false,
    isAssistant = false,
    onEntityClick,
    onEntityMentionClick,
    renderEntity,
    className = "prose max-w-[750px] w-full min-w-0",
  }) => {
    // Fix placeholder format before processing - remove newlines from placeholders
    // Also remove bullet markers when the line only contains an entity reference
    const processedContent = useMemo(
      () =>
        content
          .replace(/\{placeholder:([^:]*):([^}]*?)\n\}/g, "{placeholder:$1:$2}")
          // Remove bullet markers (- or *) when the line only contains an entity-ref
          .replace(/^[-*]\s+(<entity-ref\s+[^>]+\/>)\s*$/gm, "$1"),
      [content],
    );

    // Streaming buffer state
    const [bufferedContent, setBufferedContent] =
      useState<string>(processedContent);
    const bufferTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Buffer streaming content to prevent flashes
    useEffect(() => {
      const partialEntityRegex = /<[^>]*$/;
      const isStreamingContent = partialEntityRegex.test(processedContent);

      // Clear existing timeout
      if (bufferTimeoutRef.current) {
        clearTimeout(bufferTimeoutRef.current);
        bufferTimeoutRef.current = null;
      }

      if (isStreamingContent) {
        // During streaming, apply a small buffer delay
        bufferTimeoutRef.current = setTimeout(() => {
          setBufferedContent(processedContent);
          bufferTimeoutRef.current = null;
        }, 50); // 50ms buffer for smooth rendering
      } else {
        // For complete content, render immediately
        setBufferedContent(processedContent);
      }

      // Cleanup timeout on unmount
      return () => {
        if (bufferTimeoutRef.current) {
          clearTimeout(bufferTimeoutRef.current);
          bufferTimeoutRef.current = null;
        }
      };
    }, [processedContent]);

    // Render entity references with streaming support
    const renderEntityReference = useCallback(
      ({
        entityId,
        entityType: _,
        keyPrefix,
      }: {
        entityId: string;
        entityType: string;
        keyPrefix: string;
      }) => {
        const entity = entities.get(entityId);

        if (!entity) {
          return null;
        }

        return (
          <motion.div
            key={keyPrefix}
            className="not-prose my-2"
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <RenderEntityReference
              entity={entity}
              onClick={onEntityClick ? () => onEntityClick(entity) : undefined}
              onMentionClick={
                onEntityMentionClick
                  ? () => onEntityMentionClick(entity)
                  : undefined
              }
              customRenderer={renderEntity}
            />
          </motion.div>
        );
      },
      [entities, onEntityClick, onEntityMentionClick, renderEntity],
    );

    return (
      <div className={className}>
        <ContentWithEntities
          bufferedContent={bufferedContent}
          renderEntityReference={renderEntityReference}
          isStreaming={isStreaming}
          isAssistant={isAssistant}
        />
      </div>
    );
  },
);

MessageRenderer.displayName = "MessageRenderer";
